import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StudentSection } from "./StudentLayout";
import { Award } from "lucide-react";
import { ADULT_BELTS, KIDS_BELTS, BELT_CLASS, BELT_COLOR, beltLabel, isKidsBelt } from "@/lib/belts";

/**
 * Belt hero — renders the actual jiu-jitsu belt including the striped kids
 * variants. Uses the same CSS gradient classes as `.belt-yellow-black` etc.
 * so a kids-yellow-black student sees a real yellow belt with a black tip
 * instead of a blank / white bar.
 */
function Belt({ belt, stripes = 0, large }) {
  const w = large ? 280 : 140;
  const h = large ? 64 : 26;
  return (
    <div className={`rounded-md flex relative belt-hero ${BELT_CLASS[belt] || "belt-white"}`}
         style={{ width: w, height: h, boxShadow: "0 6px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}
         data-testid={`belt-hero-${belt}`}>
      {/* Black tip on the right (traditional jiu-jitsu belt aesthetic). Kept for
          non-striped belts; striped variants already have a colored right block
          via the CSS gradient — the tip still adds the classic dark tab. */}
      <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "32%", background: "#0e0e0e", borderTopRightRadius: 6, borderBottomRightRadius: 6, opacity: 0.9 }} />
      {/* Stripes */}
      <div style={{ position: "absolute", right: "8%", top: large ? 8 : 4, bottom: large ? 8 : 4, display: "flex", gap: large ? 5 : 3 }}>
        {[1,2,3,4].map(stripeNum => (
          <div key={`stripe-${stripeNum}`} style={{ width: large ? 8 : 4, background: stripeNum <= stripes ? "#ffffff" : "rgba(255,255,255,0.25)", borderRadius: 1 }}/>
        ))}
      </div>
    </div>
  );
}

/**
 * Progression roadmap dot for a single belt. Uses the CSS gradient class so
 * striped kids belts render authentically (yellow-black actually looks
 * yellow-with-a-black-band, not solid yellow).
 */
function BeltDot({ belt, current, dim }) {
  return (
    <div className="flex flex-col items-center gap-2" style={{ opacity: dim ? 0.35 : 1 }}>
      <div className={`${BELT_CLASS[belt] || "belt-white"}`}
           style={{
             width: 24, height: 24, borderRadius: 999,
             border: current ? "2px solid var(--green)" : "1px solid var(--border)",
           }} />
      <span className="label-tiny text-center" style={{ color: current ? "var(--green)" : "var(--text-dim)", maxWidth: 46, lineHeight: 1.15 }}>
        {beltLabel(belt).replace("-", " ")}
      </span>
    </div>
  );
}

export default function StudentBelt() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/student/belt").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-8 label">Loading…</div>;

  const nextLabel = data.next_belt;
  const stripesLeft = 4 - data.stripes;
  // Pick the roadmap track based on the current belt. Once a student crosses to
  // blue+ they're on the adult track for good.
  const onKidsTrack = isKidsBelt(data.current_belt);
  const track = onKidsTrack ? KIDS_BELTS : ADULT_BELTS;
  const currentIdx = track.indexOf(data.current_belt);

  return (
    <div data-testid="page-student-belt">
      <StudentSection section="Your rank" title="Belt"/>
      <div className="px-5">
        {/* Big belt hero */}
        <div className="card p-7 flex flex-col items-center" data-testid="belt-hero">
          <Belt belt={data.current_belt} stripes={data.stripes} large />
          <div className="text-[28px] font-bold uppercase tracking-tight mt-6">{beltLabel(data.current_belt)} belt</div>
          <div className="label mt-1">{data.stripes} / 4 stripes</div>
        </div>

        {/* Progress to next stripe / belt */}
        <div className="card p-5 mt-4">
          <div className="label">Next milestone</div>
          {nextLabel ? (
            stripesLeft > 0 ? (
              <>
                <div className="font-bold text-[16px] uppercase tracking-tight mt-2">Stripe {data.stripes + 1} of 4</div>
                <div className="label-tiny mt-1">~{data.classes_to_next} more classes to your next stripe</div>
                <div className="mt-3 h-2 rounded-full overflow-hidden" style={{background:"var(--bg-elev)"}}>
                  <div style={{ height:"100%", width: `${((30 - data.classes_to_next)/30)*100}%`, background:"var(--green)" }}/>
                </div>
              </>
            ) : (
              <>
                <div className="font-bold text-[16px] uppercase tracking-tight mt-2">{beltLabel(nextLabel)} belt</div>
                <div className="label-tiny mt-1">You&apos;ve earned 4 stripes — next graduation: your next belt.</div>
              </>
            )
          ) : (
            <div className="font-bold text-[16px] uppercase tracking-tight mt-2">Black belt mastery</div>
          )}
        </div>

        {/* Roadmap — kids get the 13-rank kids progression, adults get the 5-belt track. */}
        <div className="card p-5 mt-4">
          <div className="label mb-4">Your path {onKidsTrack ? "· Kids" : ""}</div>
          <div className="flex items-start gap-2 overflow-x-auto pb-2" data-testid="belt-roadmap">
            {track.map((b, i) => (
              <BeltDot key={b}
                       belt={b}
                       current={b === data.current_belt}
                       dim={i > currentIdx} />
            ))}
          </div>
        </div>

        {/* Promotion history */}
        <div className="card p-5 mt-4">
          <div className="label mb-3">Promotion history</div>
          {data.history.length === 0 ? (
            <div className="label-tiny" data-testid="no-history">No promotions yet. Every black belt started here.</div>
          ) : data.history.map((h, i) => (
            <div key={`${h.belt}-${h.stripes}-${h.at}`} className="py-3 flex items-center gap-3" style={{ borderBottom: i < data.history.length - 1 ? "1px solid var(--border)" : "none" }} data-testid={`hist-${i}`}>
              <Award size={18} style={{color:"var(--green)"}}/>
              <div className="flex-1">
                <div className="font-semibold uppercase tracking-tight text-[13px]">{beltLabel(h.belt)} {h.stripes ? `· ${h.stripes} stripes` : ""}</div>
                <div className="label-tiny mt-0.5">{h.at}{h.promoted_by ? ` · by ${h.promoted_by}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
